import { createApi } from '@reduxjs/toolkit/query/react'
import { Checklist, ChecklistConfig, ChecklistSelectOption } from '@/common/types/checklist.def.ts'
import {
  AddChecklistConfigRequest,
  AddChecklistRequest,
  DeleteChecklistConfigRequest,
  DownloadChecklistDataRequest,
  EditChecklistRequest,
  GetAllChecklistsRequest,
  GetChecklistByIdRequest,
  GetChecklistConfigRequest,
  RemoveChecklistsRequest,
  UpdateChecklistConfigRequest,
  UpdateChecklistItemRequest,
} from '@/features/checklist/checklist.def.ts'
import { hcPortalBaseQuery } from '@/features/baseQuery.ts'
import { HcError } from '@/common/types/api.def'
import { downloadExcel } from '@/utils/misc.ts'

export const checklistApi = createApi({
  reducerPath: 'checklist',
  baseQuery: hcPortalBaseQuery(),
  tagTypes: ['Checklist', 'ChecklistConfig'],
  endpoints: (builder) => ({
    getAllChecklists: builder.query<ChecklistSelectOption[], GetAllChecklistsRequest>({
      query: (params) => ({
        url: 'Checklist/GetAllChecklists',
        method: 'GET',
        params: params,
      }),
      providesTags: [{ type: 'Checklist', id: 'HC_LIST' }],
    }),
    getDepartmentChecklists: builder.query<ChecklistSelectOption[], GetAllChecklistsRequest>({
      query: (params) => ({
        url: 'Checklist/GetDepartmentChecklists',
        method: 'GET',
        params: params,
      }),
    }),
    getDirectReportChecklists: builder.query<ChecklistSelectOption[], GetAllChecklistsRequest>({
      query: (params) => ({
        url: 'Checklist/GetDirectReportChecklists',
        method: 'GET',
        params: params,
      }),
    }),
    getChecklistById: builder.query<Checklist, GetChecklistByIdRequest>({
      query: (params) => ({
        url: 'Checklist/GetChecklistById',
        method: 'GET',
        params: params,
      }),
      providesTags: (_, __, request) => [{ type: 'Checklist', id: request.checklistId }],
    }),
    addChecklist: builder.mutation<Checklist, AddChecklistRequest>({
      query: (request) => ({
        url: 'Checklist/AddChecklist',
        method: 'POST',
        body: request,
      }),
    }),
    editChecklist: builder.mutation<boolean, EditChecklistRequest>({
      query: (request) => ({
        url: 'Checklist/EditChecklist',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, request) => [{ type: 'Checklist', id: request.checklistId }],
    }),
    updateChecklistItem: builder.mutation<void, UpdateChecklistItemRequest>({
      query: (request) => ({
        url: 'Checklist/UpdateChecklistItem',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, request) => [{ type: 'Checklist', id: request.checklistId }],
    }),
    sendReminderEmails: builder.mutation({
      query: () => ({
        url: 'Checklist/SendEmailReminder',
        method: 'POST',
      }),
    }),
    removeChecklists: builder.mutation<void, RemoveChecklistsRequest>({
      query: (request) => ({
        url: 'Checklist/RemoveChecklists',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: [{ type: 'Checklist', id: 'HC_LIST' }],
    }),
    downloadChecklistData: builder.mutation<void, DownloadChecklistDataRequest>({
      query: (request) => ({
        url: 'Checklist/DownloadChecklistData',
        method: 'POST',
        body: request,
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadExcel('checklist_data', blob)
        },
      }),
    }),
    getAllChecklistConfigs: builder.query<ChecklistConfig[], void>({
      query: () => ({
        url: 'ChecklistConfig/GetAllChecklistConfigs',
        method: 'GET',
      }),
      providesTags: [{ type: 'ChecklistConfig', id: 'LIST' }],
    }),
    getChecklistConfig: builder.query<ChecklistConfig, GetChecklistConfigRequest>({
      query: (params) => ({
        url: 'ChecklistConfig/GetChecklistConfig',
        method: 'GET',
        params,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'ChecklistConfig', id: arg.id }],
    }),
    addChecklistConfig: builder.mutation<number, AddChecklistConfigRequest>({
      query: (request) => ({
        url: 'ChecklistConfig/AddChecklistConfig',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: [{ type: 'ChecklistConfig', id: 'LIST' }],
    }),
    updateChecklistConfig: builder.mutation<void, UpdateChecklistConfigRequest>({
      query: (request) => ({
        url: 'ChecklistConfig/UpdateChecklistConfig',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ChecklistConfig', id: 'LIST' },
        { type: 'ChecklistConfig', id: arg.id },
      ],
    }),
    deleteChecklistConfig: builder.mutation<void, DeleteChecklistConfigRequest>({
      query: (request) => ({
        url: 'ChecklistConfig/DeleteChecklistConfig',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: [{ type: 'ChecklistConfig', id: 'LIST' }],
    }),
    downloadChecklistConfigData: builder.mutation<void, DownloadChecklistDataRequest>({
      query: (request) => ({
        url: 'ChecklistConfig/DownloadChecklistConfigData',
        method: 'POST',
        body: request,
        responseHandler: async (response) => {
          if (!response.ok) {
            return await response.json() as HcError
          }
          const blob = await response.blob()
          downloadExcel('checklist_config_data', blob)
        },
      }),
    }),
  }),
})

export const {
  useGetAllChecklistsQuery,
  useGetDepartmentChecklistsQuery,
  useGetDirectReportChecklistsQuery,
  useGetChecklistByIdQuery,
  useUpdateChecklistItemMutation,
  useAddChecklistMutation,
  useEditChecklistMutation,
  useSendReminderEmailsMutation,
  useRemoveChecklistsMutation,
  useDownloadChecklistDataMutation,
  useGetAllChecklistConfigsQuery,
  useGetChecklistConfigQuery,
  useAddChecklistConfigMutation,
  useUpdateChecklistConfigMutation,
  useDeleteChecklistConfigMutation,
  useDownloadChecklistConfigDataMutation,
} = checklistApi
